import { useMemo, useState } from 'react';
import { LayoutList, Pause, Play } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import FilterChip from '../ui/FilterChip';
import Button from '../ui/Button';
import { festivalDaysFromLineup, formatDayAbbrev, voteKey } from '../../utils/lineupFormat';
import { useEventLineup, useMyArtistVotes } from '../../hooks/queries/useEventLineup';
import { mergePerformances } from '../../utils/performanceGroups';
import { loadInBar, playInBar } from '../../lib/spotifyPlayerPool';
import ArtistPlaceholder from './ArtistPlaceholder';
import CachedImg from '../ui/CachedImg';
import { PlayRowsContext, useNowPlaying } from './NowPlayingBar';

const playersOf = (row) =>
  (row.members ?? [row])
    .filter((m) => m.spotify_artist_id)
    .map((m) => ({
      spotifyId: m.spotify_artist_id,
      name: m.canonical_artist_name || m.artist_name,
    }));

/**
 * Festival event-page lineup card.
 *
 * The lineup is the reason to come to a festival's page, so it is visible to
 * everyone — signed out, signed in, going or not. It used to render only for a
 * viewer already RSVP'd going, which meant the thing that would persuade you to
 * go was hidden until after you went.
 *
 * Voting still requires both: you cannot pick artists for an event you have not
 * said you are attending, and picks are per-user so they need a session. Those
 * two gates are what `canVote` carries; the lineup itself has neither.
 */
export default function EventLineupSection({ eventId, userId, rsvpStatus }) {
  const { data: lineup = [] } = useEventLineup(eventId);
  const { data: savedVotes = [] } = useMyArtistVotes(eventId, userId);

  const [selectedDay, setSelectedDay] = useState(null);
  const navigate = useNavigate();
  const nowPlaying = useNowPlaying();
  const location = useLocation();
  // The lineup is the Sets page, everywhere: one b2b is one row, Schedule before List, picks made there. This card used
  // to open its own modal, which listed a b2b's artists twice and put List first (Sam, 2026-10-07).
  const openSets = () =>
    navigate(`/sets/${encodeURIComponent(eventId)}`, {
      state: { backTo: `${location.pathname}${location.search}` },
    });

  const festivalDays = useMemo(() => festivalDaysFromLineup(lineup), [lineup]);
  const activeDay = selectedDay ?? festivalDays[0] ?? null;

  const scopedVotes = useMemo(() => {
    if (festivalDays.length > 1 && activeDay) {
      return savedVotes.filter((v) => v.performance_day === activeDay);
    }
    return savedVotes;
  }, [savedVotes, festivalDays.length, activeDay]);

  const pickCount = scopedVotes.length;

  const previewNames = useMemo(() => {
    return scopedVotes
      .map((vote) => {
        const row = lineup.find(
          (r) =>
            r.artist_id === vote.artist_id &&
            (vote.performance_day == null || r.performance_day === vote.performance_day),
        );
        return row
          ? { key: voteKey(vote.artist_id, vote.performance_day), name: row.artist_name }
          : null;
      })
      .filter(Boolean);
  }, [scopedVotes, lineup]);

  // Picking artists needs a session and an RSVP. Reading the lineup needs
  // neither — see the note above the component.
  const canVote = rsvpStatus === 'going' && Boolean(userId);

  if (lineup.length === 0) return null;

  // Sets, not "Lineup": the card is the way into the Sets page and says so (2026-10-23). The day's top four as photo tiles,
  // each with play into the now-playing bar (Sam's pick, option 3 of 3); a festival page sells itself with faces, the
  // Sets page is the detailed list.
  const merged = mergePerformances(lineup);
  const dayRows = (activeDay ? merged.filter((r) => r.performance_day === activeDay) : merged)
    .slice()
    .sort(
      (a, b) =>
        (a.role === 'headliner' ? 0 : 1) - (b.role === 'headliner' ? 0 : 1) ||
        (a.billing_order ?? 999) - (b.billing_order ?? 999),
    );
  const tiles = dayRows.slice(0, 4);
  const moreCount = dayRows.length - tiles.length;

  return (
    <PlayRowsContext.Provider value>
      <section>
        <h2 className="lineup-section-label">SETS</h2>
        <div className="lineup-section-card">
          {festivalDays.length > 1 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {festivalDays.map((day) => (
                <FilterChip
                  key={day}
                  selected={activeDay === day}
                  onClick={() => setSelectedDay(day)}
                  pill="lg"
                  className="lineup-chip"
                >
                  {formatDayAbbrev(day)}
                </FilterChip>
              ))}
            </div>
          )}
          <div className="elp-tiles">
            {tiles.map((row) => {
              const players = playersOf(row);
              return (
                <div key={voteKey(row.artist_id, row.performance_day)} className="elp-tile">
                  {/* A tap on the tile loads the artist into the player, silent; the corner play plays (as on Sets). */}
                  <button
                    type="button"
                    className="elp-tile__open"
                    onClick={() =>
                      players.length
                        ? loadInBar(voteKey(row.artist_id, row.performance_day), players)
                        : openSets()
                    }
                  >
                    {row.spotify_image_url ? (
                      <CachedImg
                        src={row.spotify_image_url}
                        alt=""
                        className="elp-tile__img"
                        fallback={
                          <ArtistPlaceholder
                            name={row.artist_name}
                            className="elp-tile__img text-[34px]"
                          />
                        }
                      />
                    ) : (
                      <ArtistPlaceholder
                        name={row.artist_name}
                        className="elp-tile__img text-[34px]"
                      />
                    )}
                    <span className="elp-tile__name">{row.artist_name}</span>
                  </button>
                  {players.length > 0 &&
                    (() => {
                      const key = voteKey(row.artist_id, row.performance_day);
                      const current = nowPlaying.key === key;
                      const playing = current && !nowPlaying.paused;
                      return (
                        <button
                          type="button"
                          className={`artist-row__play elp-tile__play${current ? ' artist-row__play--current' : ''}${playing ? ' artist-row__play--playing' : ''}`}
                          aria-label={
                            playing ? `Pause ${row.artist_name}` : `Play ${row.artist_name}`
                          }
                          onClick={() => playInBar(key, players, current ? nowPlaying.index : 0)}
                        >
                          {playing ? (
                            <>
                              <span className="play-eq" aria-hidden>
                                <i />
                                <i />
                                <i />
                              </span>
                              <Pause
                                className="artist-row__play-pause"
                                size={14}
                                fill="currentColor"
                                strokeWidth={0}
                                aria-hidden
                              />
                            </>
                          ) : (
                            <Play
                              size={14}
                              fill="currentColor"
                              strokeWidth={0}
                              className="ml-[2px]"
                              aria-hidden
                            />
                          )}
                        </button>
                      );
                    })()}
                </div>
              );
            })}
          </div>
          {canVote && pickCount > 0 ? (
            <p className="mb-3 text-meta text-ink/80">
              You&apos;ve picked {pickCount}
              {festivalDays.length > 1 && activeDay
                ? ` on ${formatDayAbbrev(activeDay)}`
                : ''}:{' '}
              {previewNames
                .slice(0, 3)
                .map((n) => n.name)
                .join(', ')}
              {pickCount > 3 ? '…' : ''}
            </p>
          ) : moreCount > 0 ? (
            <p className="mb-3 text-meta text-ink/70">
              + {moreCount} more
              {festivalDays.length > 1 && activeDay
                ? ` on ${formatDayAbbrev(activeDay)}`
                : ' on the lineup'}
            </p>
          ) : null}
          {/* Just "Sets" with the tab's own icon (Sam, 2026-10-23): the count is already in "+ N more". */}
          <Button variant="secondary" fullWidth onClick={openSets}>
            <LayoutList className="h-[18px] w-[18px]" aria-hidden />
            Sets
          </Button>
        </div>
      </section>
    </PlayRowsContext.Provider>
  );
}
